import type { FormEvent } from 'react'
import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../auth/AuthProvider'
import { LoadingButton } from '../components/ui/LoadingButton'
import { usePageExit } from '../hooks/usePageExit'
import { formatAuthError, PASSWORD_POLICY_HINT } from '../lib/auth-errors'
import { maruyaApiPublic } from '../lib/maruya-api'
import type { InvitationPreview } from '../types/api'

export function SignUpPage() {
  const { register, confirmRegistration, login } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const inviteToken = searchParams.get('invite') ?? ''

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [step, setStep] = useState<'signup' | 'confirm'>('signup')
  const [invite, setInvite] = useState<InvitationPreview | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [loginSucceeded, setLoginSucceeded] = useState(false)

  const { isExiting, isComplete: exitComplete } = usePageExit(loginSucceeded)

  useEffect(() => {
    if (!inviteToken) {
      return
    }
    maruyaApiPublic<InvitationPreview>(
      `/v1/users/invitations/${encodeURIComponent(inviteToken)}`,
    )
      .then((body) => {
        setInvite(body)
        setEmail(body.email)
      })
      .catch(() => setInvite(null))
  }, [inviteToken])

  useEffect(() => {
    if (exitComplete) {
      navigate('/', { replace: true })
    }
  }, [exitComplete, navigate])

  async function handleSignUp(event: FormEvent) {
    event.preventDefault()
    setSubmitting(true)
    setError(null)
    try {
      await register(email.trim(), password)
      setStep('confirm')
    } catch (err) {
      setError(formatAuthError(err))
    } finally {
      setSubmitting(false)
    }
  }

  async function handleConfirm(event: FormEvent) {
    event.preventDefault()
    setSubmitting(true)
    setError(null)
    try {
      await confirmRegistration(email.trim(), code.trim())
      await login(email.trim(), password)
      setLoginSucceeded(true)
    } catch (err) {
      setSubmitting(false)
      setError(formatAuthError(err))
    }
  }

  return (
    <div
      className={[
        'page',
        'page-narrow',
        'page-transition',
        isExiting ? 'page-transition--exit' : 'page-transition--enter',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <header className="page-header">
        <p className="eyebrow">Maruya RAG</p>
        <h1>サインアップ</h1>
        {invite ? (
          <p className="lede">
            招待: {invite.email}（{invite.role_name}）
          </p>
        ) : (
          <p className="lede">
            初回登録の場合、システムにユーザーがいなければ最初のアカウントが管理者（オーナー）として登録されます。
          </p>
        )}
      </header>

      {step === 'signup' ? (
        <form
          className={['card', 'form-card', submitting ? 'form-card--busy' : '']
            .filter(Boolean)
            .join(' ')}
          onSubmit={(event) => void handleSignUp(event)}
        >
          <label className="field">
            <span>メールアドレス</span>
            <input
              type="email"
              autoComplete="email"
              required
              readOnly={Boolean(invite)}
              disabled={submitting}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </label>
          <label className="field">
            <span>パスワード</span>
            <input
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              disabled={submitting}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
            <span className="field-hint">{PASSWORD_POLICY_HINT}</span>
          </label>
          {error && <p className="form-error">{error}</p>}
          <LoadingButton
            type="submit"
            isLoading={submitting}
            loadingLabel="送信中…"
          >
            アカウント作成
          </LoadingButton>
        </form>
      ) : (
        <form
          className={['card', 'form-card', submitting ? 'form-card--busy' : '']
            .filter(Boolean)
            .join(' ')}
          onSubmit={(event) => void handleConfirm(event)}
        >
          <p className="lede">
            {email} 宛に送信された確認コード（6桁）を入力してください。
          </p>
          <label className="field">
            <span>確認コード</span>
            <input
              required
              inputMode="numeric"
              autoComplete="one-time-code"
              disabled={submitting}
              value={code}
              onChange={(event) => setCode(event.target.value)}
            />
          </label>
          {error && <p className="form-error">{error}</p>}
          <LoadingButton
            type="submit"
            isLoading={submitting}
            loadingLabel="ログイン中…"
          >
            確認してログイン
          </LoadingButton>
        </form>
      )}

      <p className="muted center-text">
        既にアカウントがある場合は <Link to="/login">ログイン</Link>
      </p>
    </div>
  )
}
